/** The pre-game level picker. Plain DOM, icon-first, and readable at phone width. */

import type { LevelCatalogEntry } from '@/levels/catalog';
import { levelAccess } from '@/systems/levelUnlocks';
import type { SaveData } from '@/systems/save';

export interface LevelSelectOptions {
  levels: readonly LevelCatalogEntry[];
  save: SaveData;
  message?: string;
  onSelect: (levelId: string) => void;
}

const EARNED_STARS = '★★★';
const EMPTY_STARS = '☆☆☆';

export class LevelSelect {
  private readonly root: HTMLElement;

  constructor(parent: HTMLElement, options: LevelSelectOptions) {
    const host = document.createElement('section');
    host.id = 'level-select';
    host.setAttribute('aria-label', 'Choose a level');
    host.innerHTML = TEMPLATE;
    parent.append(host);
    this.root = host;

    const message = mustFind(host, '#level-select-message');
    message.textContent = options.message ?? '';
    message.classList.toggle('visible', options.message !== undefined);

    const list = mustFind(host, '#level-list');
    let firstPlayable: HTMLButtonElement | null = null;
    for (const entry of options.levels) {
      const button = this.buildCard(entry, options);
      list.append(button);
      if (!button.disabled && firstPlayable === null) firstPlayable = button;
    }

    if (document.pointerLockElement) document.exitPointerLock();
    firstPlayable?.focus();
  }

  dispose(): void {
    this.root.remove();
  }

  private buildCard(entry: LevelCatalogEntry, options: LevelSelectOptions): HTMLButtonElement {
    const gate = {
      id: entry.id,
      unlockAfter: entry.unlockAfter,
      available: entry.load !== undefined,
    };
    const access = levelAccess(gate, options.save);
    const record = options.save.levels[entry.id];
    const prerequisite = options.levels.find((level) => level.id === entry.unlockAfter);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = `level-tile ${access}`;
    button.dataset.levelId = entry.id;
    button.disabled = access !== 'unlocked';
    button.setAttribute('aria-label', `Level ${entry.chapter}: ${entry.name}, ${access}`);

    const heading = document.createElement('span');
    heading.className = 'level-heading';
    heading.textContent = `Level ${entry.chapter}`;

    const icon = document.createElement('span');
    icon.className = 'level-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = access === 'locked' ? '🔒' : entry.icon;

    const name = document.createElement('strong');
    name.className = 'level-name';
    name.textContent = entry.name;

    const room = document.createElement('span');
    room.className = 'level-room';
    room.textContent = entry.room;

    const stars = document.createElement('span');
    stars.className = 'level-stars';
    const earned = record?.bestStars ?? 0;
    stars.textContent =
      EARNED_STARS.slice(0, earned) + EMPTY_STARS.slice(0, EARNED_STARS.length - earned);
    stars.setAttribute('aria-label', `${earned} best stars`);

    const score = document.createElement('span');
    score.className = 'level-best';
    score.textContent = record?.completed ? `Best ${record.bestTotal}` : 'New!';

    const action = document.createElement('span');
    action.className = 'level-action';
    if (access === 'unavailable') action.textContent = '🚧 Coming soon';
    else if (access === 'locked') {
      action.textContent = prerequisite ? `🔒 Finish Level ${prerequisite.chapter}` : '🔒 Locked';
    } else action.textContent = '🐾 Play';

    button.append(heading, icon, name, room, stars, score, action);
    if (access === 'unlocked') button.addEventListener('click', () => options.onSelect(entry.id));
    return button;
  }
}

function mustFind(root: HTMLElement, selector: string): HTMLElement {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) throw new Error(`Level select template is missing ${selector}`);
  return element;
}

const TEMPLATE = `
  <div class="level-select-panel">
    <div class="level-select-cat" aria-hidden="true">🐈‍⬛</div>
    <h1>Choose your mischief</h1>
    <p class="level-select-subtitle">Pick a room to explore</p>
    <p id="level-select-message" role="status"></p>
    <div id="level-list" class="level-list"></div>
  </div>
`;
